import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { Notification } from 'modules/core/domain/interfaces/supabase/notifications/types';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

import { AppDispatch } from 'store/store';
import Breadcrumbs from 'ud-ui/components/breadcrumbs';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { updateNotification } from 'modules/notifications/store/actions';
import NotificationForm from '../components/notification-form';
import NotificationFormSkeleton from '../components/notification-form-skeleton';
import { useNotification } from '../hooks/use-notification';
import { useNotificationsErrorReason } from '../hooks/use-notifications-error-reason';

const EditNotificationPage: React.FC = () => {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isAdmin = useIsAdmin();
  const { notification, loading, error } = useNotification(Number(id));
  const errorReason = useNotificationsErrorReason(error);
  const { setHeaderContent } = useMainLayout();

  const handleSubmit = useCallback(
    async (values: Partial<Notification>) => {
      if (!id) {
        return;
      }
      try {
        await dispatch(updateNotification({ ...values, id: Number(id) })).unwrap();
        toast.success(t`Уведомление обновлено`);
        navigate(Pages.NOTIFICATIONS.INDEX);
      } catch (error) {
        toastApiError(error, msg`Не удалось обновить уведомление`);
      }
    },
    [dispatch, navigate, id, t, toastApiError],
  );

  const handleCancel = useCallback(() => {
    navigate(Pages.NOTIFICATIONS.INDEX);
  }, [navigate]);

  const title = notification?.title;

  const breadcrumbs = useMemo(() => {
    return [
      { label: t`Уведомления`, to: Pages.NOTIFICATIONS.INDEX },
      {
        label: t`Редактирование уведомления: ${title}`,
        to: Pages.NOTIFICATIONS.builders.edit(Number(id)),
      },
    ];
  }, [id, title, t]);

  useEffect(() => {
    setHeaderContent(<Breadcrumbs breadcrumbs={breadcrumbs} />);
    return () => setHeaderContent(null);
  }, [breadcrumbs, setHeaderContent]);

  if (!isAdmin) {
    return <Navigate to={Pages.NOTIFICATIONS.INDEX} replace />;
  }

  if (error !== null) {
    return (
      <>
        <div className="w-full p-6 px-6" style={{ margin: '0 auto' }}>
          <div className="alert alert-danger">{errorReason}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="w-full p-6 px-6" style={{ margin: '0 auto' }}>
        {/* Скелетон — только пока самого уведомления ещё нет. Сохранение (`updateNotification.pending`) форму не
            размонтирует: иначе Formik терял бы набранное, а после отказа форма вставала бы заново со старыми значениями. */}
        {notification ? (
          <NotificationForm initialValues={notification} onSubmit={handleSubmit} onCancel={handleCancel} />
        ) : (
          loading && <NotificationFormSkeleton />
        )}
      </div>
    </>
  );
};

export default EditNotificationPage;
